import React from "react";
import { useBranding, brandingFallback } from "@/lib/branding";
import {
    MapPin, Phone, Mail, Facebook, Instagram, Youtube,
} from "lucide-react";

// External base — every link maps back to hccbc.com so the policies, news,
// program pages, and PDFs remain authoritative on the main marketing site.
const HCC = "https://hccbc.com";

const QUICK_LINKS = [
    { label: "Home", href: `${HCC}/index.php` },
    { label: "About Us", href: `${HCC}/about-us.php` },
    { label: "News & Events", href: `${HCC}/news-events.php` },
    { label: "Diploma Programs", href: `${HCC}/diploma-programs.php` },
    { label: "Certificate Programs", href: `${HCC}/certificate-programs.php` },
    { label: "Contact Us", href: `${HCC}/contact.php` },
];

const POLICIES = [
    { label: "Attendance Policy", href: `${HCC}/pdf/attendance-policy.pdf` },
    { label: "Dispute Resolution Policy", href: `${HCC}/pdf/dispute-resolution-policy.pdf` },
    { label: "Grades Appeal Policy", href: `${HCC}/pdf/grades-appeal-policy.pdf` },
    { label: "HCC Dismissal Policy", href: `${HCC}/pdf/hcc-dismissal-policy.pdf` },
    { label: "PAL Policy", href: `${HCC}/pdf/pal-policy.pdf` },
    { label: "Privacy Policy", href: `${HCC}/pdf/privacy-policy.pdf` },
    { label: "Respectful & Fair treatment of Students Policy", href: `${HCC}/pdf/respectful-and-fair-treatment-of-student-policy.pdf` },
    { label: "Sexual Harassment and Violence Policy", href: `${HCC}/pdf/sexual-harassment-and-violence-policy.pdf` },
    { label: "Tuition Refund Policy", href: `${HCC}/pdf/Refund-Policy.pdf` },
    { label: "Work Experience Policy", href: `${HCC}/pdf/work-experience-policy.pdf` },
    { label: "Critical incident and crisis management policy", href: `${HCC}/pdf/critical-incident-and-crisis-management-policy.pdf` },
];

// Embedded Google map for the Surrey campus (same place pin as hccbc.com).
const MAP_EMBED_SRC = "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2609.5918667353426!2d-122.86674900000001!3d49.151373!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5485d99cffed19fb%3A0x3ec9e61e1673b6da!2sHeritage%20Community%20College!5e0!3m2!1sen!2sin!4v1767070417757!5m2!1sen!2sin";

function TikTokIcon({ className = "h-4 w-4" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            className={className}
            fill="currentColor"
            aria-hidden="true"
        >
            <path d="M448.5 209.9c-44 .1-87-13.6-122.8-39.2l0 178.7c0 33.1-10.1 65.4-29 92.6s-45.6 48-76.6 59.6-64.8 13.5-96.9 5.3-60.9-25.9-82.7-50.8-35.3-56-39-88.9 2.9-66.1 18.6-95.2 40-52.7 69.6-67.7 62.9-20.5 95.7-16l0 89.9c-15-4.7-31.1-4.6-46 .4s-27.9 14.6-37 27.3-14 28.1-13.9 43.9 5.2 31 14.5 43.7 22.4 22.1 37.4 26.9 31.1 4.8 46-.1 28-14.4 37.2-27.1 14.2-28.1 14.2-43.8l0-349.4 88 0c-.1 7.4 .6 14.9 1.9 22.2 3.1 16.3 9.4 31.9 18.7 45.7s21.3 25.6 35.2 34.6c19.9 13.1 43.2 20.1 67 20.1l0 87.4z" />
        </svg>
    );
}

const SOCIAL = [
    { label: "Facebook", href: "https://www.facebook.com/HeritageCommunityCollege/", Icon: Facebook },
    { label: "YouTube", href: "https://www.youtube.com/@heritagecommunitycollege", Icon: Youtube },
    { label: "Instagram", href: "https://www.instagram.com/heritagecommunitycollege/", Icon: Instagram },
    { label: "TikTok", href: "https://www.tiktok.com/@heritagecommunitycollege", Icon: TikTokIcon },
];

export default function Footer() {
    const branding = useBranding();
    return (
        <footer
            id="site-footer"
            className="mt-24 bg-[#0c2e1f] text-white"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
                {/* Column 1 — logo, intro, contact, social */}
                <div className="lg:col-span-4 space-y-5">
                    <div className="flex items-center gap-3">
                        <img
                            src={branding.logo_url}
                            alt=""
                            className="h-14 w-14 rounded-full bg-white/95 object-contain p-1"
                            onError={brandingFallback}
                        />
                        <div className="font-display text-lg leading-tight">
                            Heritage Community College
                        </div>
                    </div>
                    <p className="text-sm text-white/75 leading-relaxed max-w-sm">
                        Empowering futures through career-focused education and real-world training.
                    </p>

                    <ul className="space-y-2.5 text-sm text-white/85" data-testid="footer-contact-list">
                        <li className="flex gap-2.5 items-start">
                            <MapPin className="h-4 w-4 mt-0.5 text-[#d4a93e] flex-shrink-0" />
                            <span>Surrey — Unit 110, 8166 128th Street Surrey, BC, V3W 1R1</span>
                        </li>
                        <li className="flex gap-2.5 items-start">
                            <MapPin className="h-4 w-4 mt-0.5 text-[#d4a93e] flex-shrink-0" />
                            <span>Victoria — 759 Courtney St, Victoria, BC, V8W 1C3</span>
                        </li>
                        <li className="flex gap-2.5 items-start">
                            <Phone className="h-4 w-4 mt-0.5 text-[#d4a93e] flex-shrink-0" />
                            <span>
                                Surrey :&nbsp;
                                <a href="tel:+16045935400" className="hover:text-[#d4a93e]" data-testid="footer-phone-surrey">
                                    +1 (604) 593-5400
                                </a>
                            </span>
                        </li>
                        <li className="flex gap-2.5 items-start">
                            <Phone className="h-4 w-4 mt-0.5 text-[#d4a93e] flex-shrink-0" />
                            <span>
                                Victoria :&nbsp;
                                <a href="tel:+12509008300" className="hover:text-[#d4a93e]" data-testid="footer-phone-victoria">
                                    +1 250 900 8300
                                </a>
                            </span>
                        </li>
                        <li className="flex gap-2.5 items-start">
                            <Mail className="h-4 w-4 mt-0.5 text-[#d4a93e] flex-shrink-0" />
                            <a href="mailto:info@hccbc.com" className="hover:text-[#d4a93e]" data-testid="footer-email">
                                info@hccbc.com
                            </a>
                        </li>
                    </ul>

                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-white/55 mb-3">
                            Social media
                        </div>
                        <ul className="flex gap-2.5" data-testid="footer-social">
                            {SOCIAL.map(({ label, href, Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        title={label}
                                        className="h-9 w-9 rounded-full border border-white/20 inline-flex items-center justify-center hover:bg-[#d4a93e] hover:text-[#0c2e1f] hover:border-[#d4a93e] transition-colors"
                                        data-testid={`footer-social-${label.toLowerCase()}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Column 2 — Quick Links */}
                <div className="lg:col-span-2">
                    <h3 className="font-display text-base mb-4 text-[#d4a93e]">Quick Links</h3>
                    <ul className="space-y-2.5 text-sm text-white/85" data-testid="footer-quick-links">
                        {QUICK_LINKS.map((l) => (
                            <li key={l.href}>
                                <a
                                    href={l.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-[#d4a93e] transition-colors"
                                >
                                    — {l.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Column 3 — Policies */}
                <div className="lg:col-span-3">
                    <h3 className="font-display text-base mb-4 text-[#d4a93e]">Quick Links</h3>
                    <ul className="space-y-2.5 text-sm text-white/85" data-testid="footer-policy-links">
                        {POLICIES.map((l) => (
                            <li key={l.href}>
                                <a
                                    href={l.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-[#d4a93e] transition-colors"
                                >
                                    — {l.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Column 4 — Location Map */}
                <div className="lg:col-span-3">
                    <h3 className="font-display text-base mb-4 text-[#d4a93e]">Location Map</h3>
                    <div className="rounded-md overflow-hidden border border-white/15 shadow-lg" data-testid="footer-map">
                        <iframe
                            src={MAP_EMBED_SRC}
                            width="100%"
                            height="320"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Heritage Community College — Surrey campus"
                        />
                    </div>
                </div>
            </div>

            {/* Footer bottom */}
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-white/65 text-center">
                    Copyright © {new Date().getFullYear()} Heritage Community College. All Rights Reserved.
                </div>
            </div>
        </footer>
    );
}
